import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { exerciseInstructions } from '../data/exerciseInstructions';
import FixedNextHelper from '../components/FixedNextHelper';
import './ExerciseDisplay.css';

/**
 * ExerciseDisplay Component
 *
 * Displays detailed information about a specific exercise, including:
 * - Title and description
 * - Video demonstration (when available)
 * - Step-by-step instructions
 * - Tips for proper form
 * - Muscle groups targeted
 *
 * @param {Object} props - Component props
 * @param {string} props.exerciseName - Name of the exercise to display
 * @param {number} props.targetReps - Target number of reps for this exercise
 * @param {number} props.completedReps - Number of reps completed for this exercise
 */
const ExerciseDisplay = ({
                             exerciseName,
                             targetReps,
                             completedReps
                         }) => {
    const [videoError, setVideoError] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoAspectRatio, setVideoAspectRatio] = useState(null);
    const [isVerticalVideo, setIsVerticalVideo] = useState(false);
    const videoRef = useRef(null);

    // Format the exercise name for display if not found in the database
    const formatExerciseName = useCallback((name) => {
        return name
            .replace(/([A-Z0-9])/g, ' $1')
            .trim()
            .replace(/^./, str => str.toUpperCase());
    }, []);

    // Get exercise information from our database - memoized to prevent re-renders
    const exercise = useMemo(() => {
        return exerciseInstructions[exerciseName] || {
            title: formatExerciseName(exerciseName),
            description: 'Perform this exercise with proper form and control.',
            steps: ['Focus on proper technique and form.'],
            tips: ['Consult a fitness professional for personalized guidance.'],
            muscleGroups: ['Various muscles'],
            videoUrl: null,
            image: null
        };
    }, [exerciseName, formatExerciseName]);

    // Handle video events
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleLoadedMetadata = () => {
            const { videoWidth, videoHeight } = video;
            const aspectRatio = videoWidth / videoHeight;
            setVideoAspectRatio(aspectRatio);
            setIsVerticalVideo(aspectRatio < 1); // Vertical if height > width
        };

        const handleLoadedData = () => {
            setIsVideoLoading(false);
            setVideoError(false);
            // Autoplay the video
            video.play().catch(console.error);
        };

        const handleError = (e) => {
            console.error(`Failed to load video for ${exerciseName}:`, e);
            setIsVideoLoading(false);
            setVideoError(true);
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('error', handleError);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('error', handleError);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, [exercise.videoUrl, exerciseName]);

    // Determine if the exercise has a video
    const hasVideo = Boolean(exercise.videoUrl);

    return (
        <div className="exercise-display" style={{ overflow: 'visible', height: 'auto' }}>
            {/* Exercise title above video */}
            <div className="exercise-header-top">
                <h2 className="exercise-title-top">{exercise.title}</h2>
            </div>

            {/* Large video section - now uses local videos with adaptive sizing */}
            <div
                className={`exercise-video-section ${isVerticalVideo ? 'vertical-video-layout' : 'horizontal-video-layout'}`}
                style={{ overflow: 'visible', height: 'auto' }}
            >
                {hasVideo && !videoError ? (
                    <div className={`large-video-container ${isVerticalVideo ? 'vertical-container' : 'horizontal-container'}`}>
                        {isVideoLoading && (
                            <div className="video-loading-overlay">
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                                <p>Loading video...</p>
                            </div>
                        )}

                        <video
                            ref={videoRef}
                            src={exercise.videoUrl}
                            loop
                            muted
                            autoPlay
                            playsInline
                            preload="metadata"
                            controls
                            crossOrigin="anonymous"
                            className={`local-exercise-video ${isVerticalVideo ? 'vertical-video' : 'horizontal-video'} ${isVideoLoading ? 'loading' : ''}`}
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '12px',
                                backgroundColor: '#000000',
                                opacity: isVideoLoading ? 0 : 1,
                                transition: 'opacity 0.3s ease',
                                objectFit: 'contain',
                                // Add these CSS properties to force proper rendering
                                WebkitTransform: 'translateZ(0)',
                                transform: 'translateZ(0)',
                                backfaceVisibility: 'hidden',
                                WebkitBackfaceVisibility: 'hidden'
                            }}
                            onLoadStart={() => {
                                console.log('Video load started for:', exerciseName);
                                setIsVideoLoading(true);
                                setVideoError(false);
                            }}
                            onCanPlay={() => {
                                console.log('Video can play:', exerciseName);
                                setIsVideoLoading(false);
                                // Try to play the video
                                const video = videoRef.current;
                                if (video) {
                                    video.play().catch(error => {
                                        console.warn('Autoplay failed:', error);
                                        // Autoplay failed, but video is ready
                                    });
                                }
                            }}
                            onError={(e) => {
                                console.error('Video error for', exerciseName, ':', e);
                                setIsVideoLoading(false);
                                setVideoError(true);
                            }}
                            onStalled={() => {
                                console.warn('Video stalled for:', exerciseName);
                            }}
                            onWaiting={() => {
                                console.log('Video waiting for data:', exerciseName);
                            }}
                        >
                            <p className="video-not-supported">
                                Your browser doesn't support video playback.
                                <a
                                    href={exercise.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3b82f6', textDecoration: 'underline', marginLeft: '4px' }}
                                >
                                    Download the video instead
                                </a>
                            </p>
                        </video>
                    </div>
                ) : videoError ? (
                    <div className="large-video-error">
                        <div className="error-content">
                            <svg
                                width="48"
                                height="48"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                style={{ color: '#ef4444', marginBottom: '12px' }}
                            >
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="error-message">Video failed to load</p>
                            <p className="error-details">{exerciseName}.mp4</p>
                            <button
                                onClick={() => {
                                    setVideoError(false);
                                    setIsVideoLoading(true);
                                    // Force video to reload
                                    const video = videoRef.current;
                                    if (video) {
                                        video.load();
                                    }
                                }}
                                className="retry-button"
                                style={{
                                    marginTop: '8px',
                                    padding: '8px 16px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                ) : exercise.image ? (
                    <div className="large-image-container">
                        <img
                            src={exercise.image}
                            alt={exercise.title}
                            className="large-exercise-image"
                        />
                    </div>
                ) : (
                    <div className="large-exercise-placeholder">
                        <span className="placeholder-text">{exercise.title}</span>
                        <p className="placeholder-subtitle">Video demonstration not available</p>
                    </div>
                )}
            </div>

            {/* Exercise instructions below video */}
            <div className="exercise-content-section" style={{ overflow: 'visible', height: 'auto' }}>
                <div className="exercise-description-box">
                    <p className="exercise-description">{exercise.description}</p>
                </div>

                <div className="exercise-instructions">
                    <div className="instructions-section">
                        <h3 className="section-title">Instructions:</h3>
                        <ol className="instruction-steps">
                            {exercise.steps.map((step, index) => (
                                <li key={index} className="step-item">
                                    <span className="step-number">{index + 1}.</span>
                                    <span className="step-text">{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="tips-section">
                        <h3 className="section-title">Tips:</h3>
                        <ul className="instruction-tips">
                            {exercise.tips.map((tip, index) => (
                                <li key={index} className="tip-item">
                                    <span className="tip-bullet">•</span>
                                    <span className="tip-text">{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="muscle-groups-section">
                        <h3 className="section-title">Primary Muscle Groups:</h3>
                        <div className="muscle-tags">
                            {exercise.muscleGroups.map((muscle, index) => (
                                <span key={index} className="muscle-tag">{muscle}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed floating next button - now handles its own navigation */}
            <FixedNextHelper />
        </div>
    );
};

export default ExerciseDisplay;